import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, FileUp, Lock, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import { Btn, INPUT, LABEL, fmtDate, localToday, errMsg, r2 } from './ui';
import { CostCentreSelect } from './ItemPicker';

// ── Multi-line journal entry with optional auto-reversal ─────────────────

const blank = () => ({ accountId: '', description: '', debit: '', credit: '', costCentreId: null });
const firstOfNextMonth = (d) => { const x = new Date(d); return new Date(x.getFullYear(), x.getMonth() + 1, 1); };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function JournalEntryView({ accounts, currency }) {
  const [f, setF] = useState({ date: localToday(), memo: '', reverse: false, reverseOn: '', lines: [blank(), blank()] });
  const [saving, setSaving] = useState(false);
  const setLine = (i, p) => setF((x) => ({ ...x, lines: x.lines.map((l, j) => (j === i ? { ...l, ...p } : l)) }));
  const dr = r2(f.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0));
  const cr = r2(f.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0));
  const balanced = dr > 0 && Math.abs(dr - cr) < 0.005;
  const save = async () => {
    setSaving(true);
    try {
      await api.post('/crm/bookkeeping/journal', {
        date: f.date, memo: f.memo, reverseOn: f.reverse ? (f.reverseOn || ymd(firstOfNextMonth(f.date))) : undefined,
        lines: f.lines.filter((l) => l.accountId && (Number(l.debit) || Number(l.credit))).map((l) => ({ accountId: l.accountId, description: l.description, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0, costCentreId: l.costCentreId || null })),
      });
      alertService.success('Journal entry posted', f.reverse ? 'It will reverse itself on the date you set.' : '');
      setF({ date: localToday(), memo: '', reverse: false, reverseOn: '', lines: [blank(), blank()] });
    } catch (e) { alertService.error('Not posted', errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="flex flex-col gap-4 max-w-5xl">
      <p className="text-sm text-[#5C5C5C]">For adjustments, accruals and corrections. Admins only. Tick “Reverse automatically” for an accrual — e.g. a cost incurred this month but billed next month.</p>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div><label className={LABEL} htmlFor="je-date">Date</label><input id="je-date" type="date" className={INPUT} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
        <div className="sm:col-span-3"><label className={LABEL} htmlFor="je-memo">Narration</label><input id="je-memo" className={INPUT} value={f.memo} onChange={(e) => setF({ ...f, memo: e.target.value })} placeholder="What this entry is for" /></div>
      </div>
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="w-full text-[12px] sm:min-w-[53.75rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr><th className="px-2 py-2 text-left w-64">Account</th><th className="px-2 py-2 text-left">Line note</th><th className="px-2 py-2 text-left w-44">Cost centre</th><th className="px-2 py-2 text-right w-28">Debit</th><th className="px-2 py-2 text-right w-28">Credit</th><th className="w-8" /></tr></thead>
          <tbody>{f.lines.map((l, i) => (
            <tr key={i} className="border-t border-[#F3F2F2]">
              <td className="px-2 py-1"><select aria-label="Account" className={INPUT} value={l.accountId} onChange={(e) => setLine(i, { accountId: e.target.value })}><option value="">Choose…</option>{accounts.filter((a) => a.isActive !== false).map((a) => <option key={a._id} value={a._id}>{a.code} · {a.name}</option>)}</select></td>
              <td className="px-2 py-1"><input aria-label="Line note" className={INPUT} value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} /></td>
              <td className="px-2 py-1"><CostCentreSelect value={l.costCentreId} onChange={(costCentreId) => setLine(i, { costCentreId })} className="w-full" /></td>
              <td className="px-2 py-1"><input aria-label="Debit" type="number" min="0" step="0.01" className={cn(INPUT, 'text-right')} value={l.debit} onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? '' : l.credit })} /></td>
              <td className="px-2 py-1"><input aria-label="Credit" type="number" min="0" step="0.01" className={cn(INPUT, 'text-right')} value={l.credit} onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? '' : l.debit })} /></td>
              <td className="px-1">{f.lines.length > 2 && <button type="button" aria-label="Remove line" className="p-1 text-[#706E6B] hover:text-[#BA0517]" onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })}><Trash2 size={13} /></button>}</td>
            </tr>
          ))}</tbody>
          <tfoot><tr className="border-t-2 border-[#D8DDE6] font-semibold"><td className="px-2 py-2" colSpan={3}><button type="button" className="text-[12px] text-[#0176D3] flex items-center gap-1" onClick={() => setF({ ...f, lines: [...f.lines, blank()] })}><Plus size={13} />Add line</button></td>
            <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(dr, currency)}</td><td className="px-2 py-2 text-right tabular-nums">{formatCurrency(cr, currency)}</td><td /></tr></tfoot>
        </table>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex items-center gap-2 text-[13px] h-9"><input type="checkbox" checked={f.reverse} onChange={(e) => setF({ ...f, reverse: e.target.checked })} />Reverse automatically</label>
        {f.reverse && <div><label className={LABEL} htmlFor="je-rev">On</label><input id="je-rev" type="date" className={INPUT} value={f.reverseOn || ymd(firstOfNextMonth(f.date))} onChange={(e) => setF({ ...f, reverseOn: e.target.value })} /></div>}
        <div className="flex-1" />
        {!balanced && dr + cr > 0 && <span className="text-[12px] text-[#BA0517]">Out by {formatCurrency(Math.abs(dr - cr), currency)}</span>}
        <Btn primary disabled={!balanced || saving} onClick={save}>{saving ? 'Posting…' : 'Post entry'}</Btn>
      </div>
    </div>
  );
}

// ── Opening balances ─────────────────────────────────────────────────────

function parseCsv(text) {
  const rows = []; let row = []; let cell = ''; let q = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (q) { if (c === '"' && text[i + 1] === '"') { cell += '"'; i += 1; } else if (c === '"') q = false; else cell += c; }
    else if (c === '"') q = true;
    else if (c === ',') { row.push(cell.trim()); cell = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i += 1; row.push(cell.trim()); cell = ''; if (row.some(Boolean)) rows.push(row); row = []; }
    else cell += c;
  }
  row.push(cell.trim()); if (row.some(Boolean)) rows.push(row);
  return rows;
}

const KINDS = {
  balances: { label: 'Account balances', url: '/crm/accounting/opening/balances', columns: ['account', 'debit', 'credit'], example: 'account,debit,credit\n1010,250000,\nAccrued Expenses,,10000', help: 'Account code or name, with its debit or credit balance. Customer and vendor balances go in the other two tabs, so each invoice and bill is tracked. The difference goes to Opening Balance Equity.' },
  invoices: { label: 'Unpaid customer invoices', url: '/crm/accounting/opening/invoices', columns: ['customer', 'invoiceNumber', 'date', 'dueDate', 'amount'], example: 'customer,invoiceNumber,date,dueDate,amount\nAcme Retail,OLD-1042,2026-02-10,2026-03-12,45000', help: 'One row per invoice still unpaid on the switch-over date. Customers are matched by name, or created.' },
  bills: { label: 'Unpaid vendor bills', url: '/crm/accounting/opening/bills', columns: ['vendor', 'billNumber', 'date', 'dueDate', 'amount'], example: 'vendor,billNumber,date,dueDate,amount\nOffice Mart,OM-77,2026-03-01,2026-03-31,12000', help: 'One row per bill still unpaid on the switch-over date. Vendors are matched by name, or created.' },
};

export function OpeningBalancesView({ currency }) {
  const [kind, setKind] = useState('balances');
  const [asOf, setAsOf] = useState(localToday());
  const [text, setText] = useState('');
  const [errors, setErrors] = useState([]);
  const [imports, setImports] = useState([]);
  const [busy, setBusy] = useState(false);
  const k = KINDS[kind];
  const load = useCallback(() => api.get('/crm/accounting/opening').then((r) => setImports(r.data.imports || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const rows = (() => {
    const parsed = parseCsv(text);
    if (!parsed.length) return [];
    const head = parsed[0].map((h) => h.toLowerCase().replace(/\s+/g, ''));
    const hasHead = k.columns.some((c) => head.includes(c.toLowerCase()));
    const cols = hasHead ? head : k.columns.map((c) => c.toLowerCase());
    return (hasHead ? parsed.slice(1) : parsed).map((r) => Object.fromEntries(k.columns.map((c) => [c, r[cols.indexOf(c.toLowerCase())] ?? ''])));
  })();
  const submit = async () => {
    setBusy(true); setErrors([]);
    try {
      const r = await api.post(k.url, { asOf, rows });
      alertService.success('Imported', kind === 'balances' ? `${r.data.lines} line(s); ${formatCurrency(r.data.toOpeningEquity, currency)} to Opening Balance Equity` : `${r.data.created} document(s), ${formatCurrency(r.data.total, currency)}`);
      setText(''); load();
    } catch (e) { setErrors(e.response?.data?.errors || []); alertService.error('Not imported', errMsg(e)); } finally { setBusy(false); }
  };
  const reverse = async (i) => {
    if (!(await alertService.confirm('Reverse these opening balances?', 'A reversing entry is posted; you can then import corrected balances.', 'warning', 'Reverse'))) return;
    try { await api.post(`/crm/accounting/opening/${i.id}/void`); load(); } catch (e) { alertService.error('Not reversed', errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-4 max-w-5xl">
      <p className="text-sm text-[#5C5C5C]">Moving from Tally, Zoho or spreadsheets? Bring in balances on your switch-over date. Admins only; each file is checked in full before anything is posted.</p>
      <div className="flex gap-1">
        {Object.entries(KINDS).map(([key, v]) => <button key={key} type="button" onClick={() => { setKind(key); setErrors([]); }} className={cn('h-8 px-3 rounded text-[12px] font-semibold border', kind === key ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#D8DDE6]')}>{v.label}</button>)}
      </div>
      <p className="text-[12px] text-[#706E6B]">{k.help}</p>
      <div className="flex flex-wrap items-end gap-2">
        {kind === 'balances' && <div><label className={LABEL} htmlFor="ob-asof">Balances as of</label><input id="ob-asof" type="date" className={INPUT} value={asOf} onChange={(e) => setAsOf(e.target.value)} /></div>}
        <label className="h-9 px-3 rounded border border-[#D8DDE6] bg-white text-[13px] font-semibold flex items-center gap-1 cursor-pointer"><FileUp size={14} />Load CSV<input type="file" accept=".csv,text/csv" className="hidden" onChange={async (e) => { const file = e.target.files?.[0]; if (file) setText(await file.text()); e.target.value = ''; }} /></label>
        <Btn onClick={() => setText(k.example)}>Show example</Btn>
      </div>
      <textarea aria-label="CSV rows" rows={8} className={cn(INPUT, 'h-auto py-2 font-mono text-[12px]')} value={text} onChange={(e) => setText(e.target.value)} placeholder={k.example} />
      {rows.length > 0 && <p className="text-[12px] text-[#706E6B]">{rows.length} row(s) ready{kind === 'balances' ? ` · debits ${formatCurrency(rows.reduce((s, r) => s + (Number(r.debit) || 0), 0), currency)}, credits ${formatCurrency(rows.reduce((s, r) => s + (Number(r.credit) || 0), 0), currency)}` : ''}.</p>}
      {errors.length > 0 && (
        <div className="bg-[#FEF1EE] border border-[#F8B6A8] rounded p-3 text-[12px] text-[#8C1B0F]">
          {errors.map((e, i) => <div key={i}>{e.row ? `Row ${e.row}: ` : ''}{e.message}</div>)}
        </div>
      )}
      <div><Btn primary disabled={!rows.length || busy} onClick={submit}>{busy ? 'Importing…' : `Import ${k.label.toLowerCase()}`}</Btn></div>
      {imports.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-[13px] font-semibold">Account balance imports</h3>
          <div className="bg-white border border-[#D8DDE6] rounded-lg divide-y divide-[#EEF1F6]">
            {imports.map((i) => (
              <div key={i.id} className={cn('flex items-center justify-between px-3 py-2 text-[12px]', i.voided && 'opacity-50')}>
                <span>{i.memo} · {i.lines} lines · {formatCurrency(i.total, currency)} · by {i.by || '—'} on {fmtDate(i.createdAt)}{i.voided && ' · reversed'}</span>
                {!i.voided && <button type="button" className="text-[#BA0517] hover:underline" onClick={() => reverse(i)}>Reverse</button>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ── Tally export ─────────────────────────────────────────────────────────

export function TallyExportView() {
  const now = new Date();
  const fy = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const [range, setRange] = useState({ from: `${fy}-04-01`, to: localToday() });
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get('/crm/accounting/tally', { params: range, responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = href; a.download = `Tally-${range.from}-to-${range.to}.xml`; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
      alertService.success('Export ready', `${res.headers['x-voucher-count'] || 'All'} vouchers`);
    } catch (e) {
      let msg = 'Please try again';
      try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch (x) { /* not JSON */ }
      alertService.error('Export failed', msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <p className="text-sm text-[#5C5C5C]">Download your day book as a Tally XML file, for your accountant or for filing from Tally.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div><label className={LABEL} htmlFor="tl-from">From</label><input id="tl-from" type="date" className={INPUT} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></div>
        <div><label className={LABEL} htmlFor="tl-to">To</label><input id="tl-to" type="date" className={INPUT} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
        <Btn primary disabled={busy} onClick={run}><Download size={14} className="inline -mt-0.5 mr-1" />{busy ? 'Preparing…' : 'Download Tally XML'}</Btn>
      </div>
      <ul className="text-[12px] text-[#706E6B] list-disc pl-5 space-y-1">
        <li>Includes the ledgers used (customers under Sundry Debtors, vendors under Sundry Creditors, banks under Bank Accounts) and one voucher per entry: Sales, Purchase, Receipt, Payment, Contra, Credit/Debit Note or Journal.</li>
        <li>In Tally: Gateway of Tally → Import → Masters and Vouchers, and choose this file. Make sure the company name in Tally matches.</li>
        <li>Amounts are in your base currency. Import into a test company first to check how ledgers map before importing into live books.</li>
      </ul>
    </div>
  );
}

// ── Cash flow statement ──────────────────────────────────────────────────

const fyStart = () => { const n = new Date(); return `${n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1}-04-01`; };

export function CashFlowView({ currency }) {
  const [range, setRange] = useState({ from: fyStart(), to: localToday() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/accounting/cash-flow', { params: range })
      .then((r) => setData(r.data)).catch((e) => alertService.error('Could not load cash flow', errMsg(e)))
      .finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);
  const ccy = data?.currency || currency;
  const money = (v) => formatCurrency(v || 0, ccy);
  const Section = ({ title, s }) => (
    <div className="border border-[#E5E5E5] rounded-lg overflow-hidden">
      <div className="px-4 py-2 bg-[#F3F3F3] text-[12px] font-semibold uppercase tracking-wide text-[#5C5C5C]">{title}</div>
      {(s?.rows || []).length === 0 && <div className="px-4 py-2 text-sm text-[#706E6B]">No movement</div>}
      {(s?.rows || []).map((r) => (
        <div key={r.label} className="flex justify-between px-4 py-1.5 text-sm"><span className="text-[#444444]">{r.label}</span><span className={cn('tabular-nums', r.amount < 0 && 'text-[#BA0517]')}>{money(r.amount)}</span></div>
      ))}
      <div className="flex justify-between px-4 py-2 border-t border-[#E5E5E5] text-sm font-semibold"><span>Net cash from {title.toLowerCase()}</span><span className={cn('tabular-nums', (s?.total || 0) < 0 && 'text-[#BA0517]')}>{money(s?.total)}</span></div>
    </div>
  );
  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <div className="flex flex-wrap items-end gap-2">
        <div><label className={LABEL} htmlFor="cf-from">From</label><input id="cf-from" type="date" className={INPUT} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></div>
        <div><label className={LABEL} htmlFor="cf-to">To</label><input id="cf-to" type="date" className={INPUT} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
        {loading && <span className="text-sm text-[#706E6B] pb-2">Loading…</span>}
      </div>
      {data && (
        <>
          <div className="flex justify-between px-4 py-2 rounded-lg bg-[#F3F3F3] text-sm"><span>Cash and bank at the start</span><span className="tabular-nums font-medium">{money(data.openingCash)}</span></div>
          <Section title="Operating activities" s={data.operating} />
          <Section title="Investing activities" s={data.investing} />
          <Section title="Financing activities" s={data.financing} />
          {(data.opening?.rows || []).length > 0 && <Section title="Opening balances" s={data.opening} />}
          <div className="flex flex-col gap-1 px-4 py-3 rounded-lg border border-[#E5E5E5] text-sm">
            <div className="flex justify-between"><span>Net change in cash</span><span className={cn('tabular-nums font-semibold', data.netChange < 0 && 'text-[#BA0517]')}>{money(data.netChange)}</span></div>
            <div className="flex justify-between"><span>Cash and bank at the end</span><span className="tabular-nums font-semibold">{money(data.closingCash)}</span></div>
            <div className="flex justify-between text-[#5C5C5C]"><span>Net profit for the same period</span><span className="tabular-nums">{money(data.netProfit)}</span></div>
          </div>
          {!data.reconciles && <p className="text-[12px] text-[#8C4B02]">The closing figure differs from your cash and bank ledgers — check for entries touching cash in a foreign currency.</p>}
          {(data.months || []).length > 1 && (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="text-left text-[12px] text-[#5C5C5C]"><th className="py-1">Month</th><th className="text-right">In</th><th className="text-right">Out</th><th className="text-right">Net</th></tr></thead>
              <tbody>{data.months.map((m) => (
                <tr key={m.month} className="border-t border-[#E5E5E5]"><td className="py-1">{m.month}</td><td className="text-right tabular-nums">{money(m.in)}</td><td className="text-right tabular-nums">{money(m.out)}</td><td className={cn('text-right tabular-nums', m.net < 0 && 'text-[#BA0517]')}>{money(m.net)}</td></tr>
              ))}</tbody>
            </table></div>
          )}
        </>
      )}
    </div>
  );
}

// ── Year-end close ───────────────────────────────────────────────────────

export function YearEndCloseView({ currency }) {
  const isAdmin = useSelector((s) => s.auth?.isAdmin);
  const lastMarch = () => { const n = new Date(); const y = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1; return `${y}-03-31`; };
  const [asOf, setAsOf] = useState(lastMarch());
  const [list, setList] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const money = (v) => formatCurrency(v || 0, currency);
  const load = useCallback(() => {
    api.get('/crm/accounting/year-end/checklist', { params: { asOf } }).then((r) => setList(r.data)).catch((e) => alertService.error('Could not load checklist', errMsg(e)));
    api.get('/crm/accounting/year-end/history').then((r) => setHistory(r.data.closes || [])).catch(() => {});
  }, [asOf]);
  useEffect(() => { load(); }, [load]);
  const revalue = async () => {
    setBusy(true);
    try {
      const r = await api.post('/crm/accounting/fx/revalue', { asOf });
      if (r.data.posted) alertService.success('Revalued', `Net ${money(r.data.net)} on ${r.data.details.length} open documents; reverses the next day`);
      else alertService.success('Nothing to revalue', r.data.message);
      load();
    } catch (e) { alertService.error('Not revalued', errMsg(e)); } finally { setBusy(false); }
  };
  const close = async () => {
    const ok = await alertService.confirm('Close the books?', `Profit of ${money(list.toClose.netIncome)} moves to retained earnings and nothing dated on or before ${fmtDate(asOf)} can be changed afterwards.`, 'warning', 'Close Books');
    if (!(ok?.isConfirmed ?? ok)) return;
    setBusy(true);
    try {
      const r = await api.post('/crm/accounting/year-end/close', { asOf, confirm: true });
      alertService.success('Books closed', `${money(r.data.netIncome)} moved to retained earnings`);
      load();
    } catch (e) { alertService.error('Not closed', errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <p className="text-sm text-[#5C5C5C]">Check the books, revalue foreign-currency balances, then close the year. Closing moves the year's profit to retained earnings and locks everything dated on or before the close date.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div><label className={LABEL} htmlFor="ye-asof">Close as of</label><input id="ye-asof" type="date" className={INPUT} value={asOf} onChange={(e) => setAsOf(e.target.value)} /></div>
        <Btn disabled={busy} onClick={revalue}><RefreshCw size={14} className="inline -mt-0.5 mr-1" />Revalue foreign currency</Btn>
      </div>
      {list && (
        <>
          {list.alreadyClosed && <div className="px-4 py-2 rounded-lg bg-[#F3F3F3] text-sm">Books are already closed up to {fmtDate(list.lastClose.asOf)}.</div>}
          <div className="border border-[#E5E5E5] rounded-lg divide-y divide-[#F3F2F2]">
            {list.items.map((i) => (
              <div key={i.key} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                {i.ok ? <CheckCircle2 size={16} className="text-[#2E844A] mt-0.5 shrink-0" /> : <AlertTriangle size={16} className={cn('mt-0.5 shrink-0', i.blocking ? 'text-[#BA0517]' : 'text-[#DD7A01]')} />}
                <div className="flex-1">
                  <div className="text-[#2E2E2E]">{i.label}{!i.ok && i.count ? ` — ${i.count}` : ''}{i.blocking && !i.ok && <span className="ml-2 text-[11px] text-[#BA0517] font-medium">must fix</span>}</div>
                  {!i.ok && i.hint && <div className="text-[12px] text-[#5C5C5C]">{i.hint}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1 px-4 py-3 rounded-lg border border-[#E5E5E5] text-sm">
            <div className="text-[12px] text-[#5C5C5C]">To close{list.lastClose ? ` (since ${fmtDate(list.lastClose.asOf)})` : ''}</div>
            <div className="flex justify-between"><span>Revenue</span><span className="tabular-nums">{money(list.toClose.revenue)}</span></div>
            <div className="flex justify-between"><span>Expenses</span><span className="tabular-nums">{money(list.toClose.expenses)}</span></div>
            <div className="flex justify-between font-semibold"><span>Net profit to retained earnings</span><span className={cn('tabular-nums', list.toClose.netIncome < 0 && 'text-[#BA0517]')}>{money(list.toClose.netIncome)}</span></div>
          </div>
          {isAdmin
            ? <div><Btn primary disabled={busy || !list.ready || list.alreadyClosed} onClick={close}><Lock size={14} className="inline -mt-0.5 mr-1" />Close books as of {fmtDate(asOf)}</Btn></div>
            : <p className="text-[12px] text-[#5C5C5C]">Only an admin can close the books.</p>}
        </>
      )}
      {history.length > 0 && (
        <div>
          <div className="text-[12px] font-semibold uppercase tracking-wide text-[#5C5C5C] mb-1">Past closes</div>
          {history.map((h) => <div key={h._id} className="flex justify-between text-sm py-1 border-t border-[#E5E5E5]"><span>{fmtDate(h.asOf)}</span><span className="tabular-nums">{money(h.netIncomeClosed)}</span></div>)}
        </div>
      )}
    </div>
  );
}
